import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DraftPropertyListDto, PropertyDto } from '@workspace/data-types';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { finalize, tap } from 'rxjs';

import { PropertyDraftListComponent } from '../../../property-drafts';

@Component({
  selector: 'etxe-property-creation-welcome',
  imports: [CommonModule, MatButtonModule, MatIconModule, PropertyDraftListComponent],
  templateUrl: './creation-welcome.component.html',
  styles: [
    `
      :host {
        display: block;
        background: radial-gradient(circle at top right, rgba(var(--primary-rgb), 0.03), transparent);
      }
      .shadow-soft {
        box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.08);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCreationWelcomeComponent implements OnInit {
  private api = inject(ApiService);
  private userStore = inject(UserStore);

  user = this.userStore.currentUser;

  newClicked = output<void>();
  draftSelected = output<PropertyDto>();

  draftProperties = signal<DraftPropertyListDto[]>([]);
  loading = signal<boolean>(false);

  ngOnInit(): void {
    this.fetchDrafts();
  }

  fetchDrafts() {
    this.loading.set(true);
    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .drafts()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((drafts) => this.draftProperties.set(drafts));
  }

  selectDraft(property: DraftPropertyListDto) {
    this.loading.set(true);
    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .get(property._id)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((fullProperty) => this.draftSelected.emit(fullProperty));
  }

  deleteDraft(property: DraftPropertyListDto) {
    this.loading.set(true);
    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .deleteState(property._id)
      .pipe(
        tap(() => this.draftProperties.update((drafts) => drafts.filter((d) => d._id !== property._id))),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }
}
